import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * WishlistIcon — mirrors CartIcon from cart-icon.js.
 *
 * Listens for 'wishlist:update' events dispatched by wishlist-form.js
 * and animates the count bubble in the header identically to the cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistBubble
 * @property {HTMLElement} wishlistBubbleText
 * @property {HTMLElement} wishlistBubbleCount
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleText', 'wishlistBubbleCount'];

  /** @returns {number} */
  get currentCount() {
    return parseInt(this.refs.wishlistBubbleCount.textContent ?? '0', 10);
  }

  set currentCount(value) {
    this.refs.wishlistBubbleCount.textContent = value < 100 ? String(value) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('wishlist:update', this.#onWishlistUpdate);
    window.addEventListener('storage', this.#onStorageChange);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#syncCount(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('wishlist:update', this.#onWishlistUpdate);
    window.removeEventListener('storage', this.#onStorageChange);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  // ── Private ───────────────────────────────────────────────────────────────

  /** Reads current count straight from localStorage */
  #getStoredCount() {
    try {
      const raw = localStorage.getItem('theme_wishlist');
      if (!raw) return 0;
      const { items } = JSON.parse(raw);
      return Array.isArray(items) ? items.length : 0;
    } catch {
      return 0;
    }
  }

  #syncCount = (animate = true) => {
    const count = this.#getStoredCount();
    this.#renderBubble(count, animate);
  };

  #onWishlistUpdate = () => {
    this.#syncCount(true);
  };

  #onStorageChange = (/** @type {StorageEvent} */ e) => {
    if (e.key !== 'theme_wishlist') return;
    this.#syncCount(false);
  };

  #onPageShow = (/** @type {PageTransitionEvent} */ e) => {
    if (e.persisted) this.#syncCount(false);
  };

  /**
   * @param {number} count
   * @param {boolean} animate
   */
  #renderBubble = async (count, animate) => {
    const { wishlistBubble, wishlistBubbleCount } = this.refs;

    wishlistBubbleCount.classList.toggle('hidden', count === 0);
    wishlistBubble.classList.toggle('visually-hidden', count === 0);
    this.currentCount = count;

    if (!animate || count === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));
    wishlistBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.wishlistBubbleText);
    wishlistBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
